import { ErrorDetails } from "../loggingSvc";
import { PerformanceMeasureService, PerformanceMeasureServiceImpl } from "./performance-measure";
import { CustomKeyValues } from "../../../ShellCommon/logger/telemetry-service";

// Plan is to remove Performance related exports once everybody starts using Scenario interfaces.
/** Records performance information and reports it up to the telemetry pipeline.  */
export interface PerformanceEventRecorder {
  /** used to mark the start of a performance event, it should not be called more than once concurrently.
   * @param userActionTriggered Whether the event recorded is part of user action like button click.
   * @param data Any data which should be captured as part of the event. Should be key-value pairs.
   * @return If the recording start was successful.
   * */
  start(userActionTriggered?: boolean, data?: CustomKeyValues): boolean;

  /** Marks and sends telemetry when a performance event has finished recording on a success case.
   * @param userActionTriggered Whether the event recorded is part of user action like button click.
   * @param data Any data which should be captured as part of the event. Should be key-value pairs.
   * @return If the recording success was successful.
   * */
  success(userActionTriggered?: boolean, data?: CustomKeyValues): boolean;

  /** Marks and sends telemetry when a performance event has finished recording on an error case.
   * @param error The error object associated with this failure, it is recommended this error is debounced for easier aggregation.
   * @param userActionTriggered Whether the event recorded is part of user action like button click.
   * @param data Any data which should be captured as part of the event. Should be key-value pairs.
   * @return If the recording failure was successful.
   * */
  failure(error: {} | string | ErrorDetails, userActionTriggered?: boolean, data?: CustomKeyValues): boolean;

  /** Marks and sends telemetry when a performance event has a checkpoint event.
   * @param userActionTriggered Whether the event recorded is part of user action like button click.
   * @param data Any data which should be captured as part of the event. Should be key-value pairs.
   * @return If the checkpoint recording was successful.
   * */
  checkpoint?(userActionTriggered: boolean, data?: CustomKeyValues): boolean;

  /** Marks and sends telemetry when a event is canceled. NOTE: Cancel should not be used when there's a failure, use failure API instead.
   * @param userActionTriggered Whether the event recorded is part of user action like button click.
   * @param data Any data which should be captured as part of the event. Should be key-value pairs.
   * @return If the recording cancel was successful.
   * */
  cancel?(userActionTriggered: boolean, data?: CustomKeyValues): boolean;
}

/** Options for a performance event recorder */
export interface PerformanceEventRecorderOptions {
  /** If the event spans across multiple browser sessions, windows or tabs.  The duration of the result does not include time not on the Pylon website,
   *  that data is captured within the wallClockDurationMs measure.
   * Otherwise, the event is expected to happen within the same browser session and adds performance markers within the browser developer tools.
   * Optional, defaults to false.
   * */
  isCrossWindowOrTab?: boolean;

  /** If the application bootstrap time included when recording the start time of this event.
   */
  shouldIncludeBootstrapTimeOnStart?: boolean;

  /** If a cross window event should not report time that was spent between the windows,
   * such as to ignore the time spend while the user is typing in login credentials
   * off of the ServiceDesk website
   * */
  shouldReportNonWallClockTime?: boolean;

  /** If this event should only be recorded once per the application lifetime.
   * Otherwise the event can be reported mutliple times.
   * Optional, defaults to false.
   * */
  recordOncePerAppLifetime?: boolean;
  /** If a cross window event, this will only record the page load time of the scenario. Exclude NonWallClockTime.
   * Optional, defaults to false.
   */
  isRecordPageLoadTime?: boolean;
  /** Detailed description of what this performance event recorder is capturing.
   * Can also have any additional telemetry data which can be used for slicing/dicing
   * Optional, defaults to undefined.
   * */
  description?: string | {};
}

/** Records scenario information and reports it up to the telemetry pipeline.  */
export interface ScenarioEventRecorder extends PerformanceEventRecorder {}

/** Options for a scenario event recorder */
export interface ScenarioEventRecorderOptions extends PerformanceEventRecorderOptions {
  /** Any telemetry data which needs to be logged for this event. If there is no override during start/success/failure, the same data
   * will be logged in the pageTags
   * */
  scenarioData?: CustomKeyValues;

  /** When set, ContentUpdate Events will be logged on start, checkpoint and success/failure. Else, a single event
   * will be logged on ending the scenario. This flag will be ignored when isCrossWindowOrTab is set.
   * */
  alwaysLog?: boolean;
}

export class PerformanceEventImpl implements PerformanceEventRecorder {
  public timesRecorded = 0;
  private uniqueIdentifier: string;

  // @ngInject
  constructor(
    private performanceLogger: PerformanceMeasureServiceImpl,
    public name: string,
    public options: ScenarioEventRecorderOptions,
    public serviceName?: string,
    public identifier?: string | number
  ) {
    this.uniqueIdentifier = identifier ? `${name}_${identifier}` : name;
  }

  public start(userActionTriggered?: boolean, data?: CustomKeyValues): boolean {
    return this.ifShouldRecord(() => this.performanceLogger.recordStartedEvent(this, data, userActionTriggered));
  }

  public success(userActionTriggered?: boolean, data?: CustomKeyValues): boolean {
    return this.ifShouldRecord(() =>
      this.onSuccessUpdateTimesRecorded(() =>
        this.performanceLogger.recordSuccessEvent(this, data, userActionTriggered)
      )
    );
  }

  public checkpoint(userActionTriggered: boolean, data?: CustomKeyValues): boolean {
    return this.ifShouldRecord(() => this.performanceLogger.recordCheckpointEvent(this, userActionTriggered, data));
  }

  public failure(error: {} | string | ErrorDetails, userActionTriggered?: boolean, data?: CustomKeyValues): boolean {
    if (typeof error !== "string") {
      error = JSON.stringify(error);
    }
    return this.ifShouldRecord(() =>
      this.onSuccessUpdateTimesRecorded(() =>
        this.performanceLogger.recordFailureEvent(this, <any>error, data, userActionTriggered)
      )
    );
  }

  public cancel(userActionTriggered: boolean, data?: CustomKeyValues): boolean {
    return this.ifShouldRecord(() =>
      this.onSuccessUpdateTimesRecorded(() => this.performanceLogger.recordCancelEvent(this, userActionTriggered, data))
    );
  }

  private ifShouldRecord(recorder: () => boolean) {
    if (this.options.recordOncePerAppLifetime && this.timesRecorded !== 0) {
      return false;
    }
    return recorder();
  }

  private onSuccessUpdateTimesRecorded(recorder: () => boolean): boolean {
    let recorded = recorder();
    if (recorded) {
      this.timesRecorded++;
    }
    return recorded;
  }

  public get startedName() {
    return this.name + "StartedEvent";
  }
  public get successName() {
    return this.name + "SuccessEvent";
  }
  public get failureName() {
    return this.name + "FailureEvent";
  }
  public get startKey() {
    return "SD" + this.name + "EventStartTime";
  }
  public get startDurationLocalStorageKey() {
    return "SD" + this.name + "EventStartDurationTime";
  }
}
